import React, { useEffect, useState } from 'react';
import { Flex, useDisclosure } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';

import { FilterPill } from './components/FilterPill';
import { FilterTogglePill } from './components/FilterTogglePill';
import { isToggleFacet } from './Filters.constants';
import type { FilterFacet } from './Filter.types';
import { FiltersDrawer } from './FiltersDrawer';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { MobileFiltersTrigger } from './components/MobileFiltersTrigger';
import { useFilterChangeHandler } from './useFilterChangeHandler';
import { useVisibleFilterFacets } from './useVisibleFilterFacets';

type FiltersProps = Readonly<{
  facets: FilterFacet[];
  pillHeight?: number;
  /** Which facet's rail is open, the toggle a pill click runs, and the rail's `id`. */
  facetSidebar: Readonly<{ activeGroupCode: string | null; onToggle: (groupCode: string) => void; railId: string }>;
  /**
   * Rendered after the pills inside the same wrapping row (the reset button), so it flows
   * beside the last pill instead of wrapping as a separate item onto a line of its own.
   */
  trailing?: React.ReactNode;
}>;

/**
 * The filter controls: one pill per presented facet on desktop, or a single drawer trigger on
 * mobile/tablet. Every presented facet gets a pill — there are at most four and they fit beside
 * the search bar, so there is no overflow row.
 */
export const Filters = React.memo((props: FiltersProps) => {
  const { tq } = useTranslation('stationery');
  const { facets, pillHeight, facetSidebar, trailing } = props;

  const [defaultActiveDrawerIndex, setDefaultActiveDrawerIndex] = useState(0);

  const isMobileOrTablet = useIsMobileScreen();
  const { isOpen: isDrawerOpen, onOpen: openDrawer, onClose: closeDrawer } = useDisclosure();

  const visibleFacets = useVisibleFilterFacets(facets);

  useEffect(() => {
    if (!isMobileOrTablet) {
      closeDrawer();
    }
  }, [isMobileOrTablet, closeDrawer]);

  const handleOnFilterChange = useFilterChangeHandler();

  const handleOnTriggerFilterDrawer = useEventCallback((groupCode?: string) => {
    if (groupCode) {
      const targetFacetIndex = visibleFacets.findIndex(facet => facet.groupCode === groupCode);
      setDefaultActiveDrawerIndex(targetFacetIndex !== -1 ? targetFacetIndex : 0);
    } else {
      setDefaultActiveDrawerIndex(-1);
    }

    openDrawer();
  });

  if (visibleFacets.length === 0) {
    return <>{trailing}</>;
  }

  return (
    <Flex
      flexWrap="wrap"
      alignItems="center"
      gap={4}
    >
      {isMobileOrTablet ? (
        <MobileFiltersTrigger
          label={tq`Filters`}
          onClick={() => {
            handleOnTriggerFilterDrawer();
          }}
        />
      ) : (
        <>
          {visibleFacets.map(facet =>
            isToggleFacet(facet.groupCode) ? (
              <FilterTogglePill
                key={facet.groupCode}
                facet={facet}
                pillHeight={pillHeight}
              />
            ) : (
              <FilterPill
                key={facet.groupCode}
                facet={facet}
                onTrigger={facetSidebar.onToggle}
                isRailOpen={facetSidebar.activeGroupCode === facet.groupCode}
                railId={facetSidebar.railId}
                pillHeight={pillHeight}
              />
            ),
          )}
        </>
      )}
      {trailing}

      <FiltersDrawer
        isOpen={isDrawerOpen}
        onChange={handleOnFilterChange}
        defaultIndex={defaultActiveDrawerIndex}
        onClose={closeDrawer}
        filterFacets={visibleFacets}
      />
    </Flex>
  );
});
